'use client';

import { Building2, Quote, ArrowUpRight, Calendar, Target } from 'lucide-react';

// Case Study Card - featured story highlight
interface CaseStudyCardProps {
  company: string;
  logo?: string;
  industry?: string;
  challenge: string;
  result: string;
  metrics?: Array<{ label: string; value: string }>;
  href?: string;
}

export function CaseStudyCard({
  company,
  logo,
  industry,
  challenge,
  result,
  metrics,
  href,
}: CaseStudyCardProps) {
  const Wrapper = href ? 'a' : 'div';
  const wrapperProps = href
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <div className="my-10 not-prose">
      <Wrapper
        {...wrapperProps}
        className={`block bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8 text-white ${href ? 'hover:from-gray-800 hover:to-gray-700 transition-all group' : ''}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            {logo ? (
              <img
                src={logo}
                alt={company}
                className="w-12 h-12 rounded-lg bg-white p-2 object-contain"
              />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white/60" />
              </div>
            )}
            <div>
              <p className="font-semibold text-lg">{company}</p>
              {industry && (
                <p className="text-sm text-gray-400">{industry}</p>
              )}
            </div>
          </div>
          {href && (
            <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
          )}
        </div>

        {/* Challenge */}
        <div className="mb-4">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
            Challenge
          </p>
          <p className="text-gray-300 leading-relaxed">{challenge}</p>
        </div>

        {/* Result */}
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wider text-amber-500 mb-1">
            Result
          </p>
          <p
            className="text-xl font-normal text-white leading-snug"
            style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
          >
            {result}
          </p>
        </div>

        {/* Metrics */}
        {metrics && metrics.length > 0 && (
          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
            {metrics.map((metric, index) => (
              <div key={index}>
                <p
                  className="text-2xl font-normal text-amber-400"
                  style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
                >
                  {metric.value}
                </p>
                <p className="text-xs text-gray-400">{metric.label}</p>
              </div>
            ))}
          </div>
        )}
      </Wrapper>
    </div>
  );
}

// Simple result card (lighter)
interface ResultCardProps {
  company: string;
  result: string;
  timeframe?: string;
  context?: string;
}

export function ResultCard({
  company,
  result,
  timeframe,
  context,
}: ResultCardProps) {
  return (
    <div className="my-8 not-prose">
      <div className="bg-gray-50 border border-gray-100 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
            <Target className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900 mb-1">{company}</p>
            <p
              className="text-lg text-gray-700"
              style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
            >
              {result}
            </p>
            {(timeframe || context) && (
              <p className="text-sm text-gray-500 mt-2">
                {timeframe && <span>{timeframe}</span>}
                {timeframe && context && <span> · </span>}
                {context && <span>{context}</span>}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Client quote / testimonial
interface ClientQuoteProps {
  quote: string;
  author: string;
  role?: string;
  company?: string;
  avatar?: string;
}

export function ClientQuote({
  quote,
  author,
  role,
  company,
  avatar,
}: ClientQuoteProps) {
  return (
    <figure className="my-10 not-prose">
      <div className="relative bg-gray-50 rounded-2xl p-6 md:p-8">
        {/* Quote mark */}
        <Quote className="absolute top-4 right-4 w-8 h-8 text-gray-200" />

        <blockquote
          className="text-xl md:text-2xl text-gray-900 leading-relaxed mb-6 relative z-10"
          style={{ fontFamily: 'var(--font-instrument), Georgia, serif' }}
        >
          &ldquo;{quote}&rdquo;
        </blockquote>

        <figcaption className="flex items-center gap-4">
          {avatar ? (
            <img
              src={avatar}
              alt={author}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <span className="text-amber-700 font-semibold text-lg">
                {author.charAt(0)}
              </span>
            </div>
          )}
          <div>
            <p className="font-semibold text-gray-900">{author}</p>
            {(role || company) && (
              <p className="text-sm text-gray-500">
                {role}
                {role && company && ', '}
                {company}
              </p>
            )}
          </div>
        </figcaption>
      </div>
    </figure>
  );
}

// Company mention (inline)
interface CompanyMentionProps {
  name: string;
  href?: string;
}

export function CompanyMention({ name, href }: CompanyMentionProps) {
  const content = (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-gray-100 rounded text-sm font-medium text-gray-700 not-prose">
      <Building2 className="w-3 h-3" />
      {name}
      {href && <ArrowUpRight className="w-3 h-3 text-gray-400" />}
    </span>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-80 transition-opacity"
      >
        {content}
      </a>
    );
  }

  return content;
}

// Timeline for case study progression
interface TimelineEvent {
  date: string;
  title: string;
  description?: string;
}

interface CaseTimelineProps {
  events: TimelineEvent[];
}

export function CaseTimeline({ events }: CaseTimelineProps) {
  return (
    <div className="my-10 not-prose">
      <div className="relative">
        {/* Line */}
        <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />

        {/* Events */}
        <div className="space-y-6">
          {events.map((event, index) => (
            <div key={index} className="relative flex gap-6 pl-12">
              {/* Dot */}
              <div
                className={`absolute left-2.5 w-3 h-3 rounded-full border-2 ${
                  index === events.length - 1
                    ? 'bg-amber-400 border-amber-400'
                    : 'bg-white border-gray-300'
                }`}
                style={{ top: '6px' }}
              />

              <div className="flex-1 pb-6">
                <div className="flex items-center gap-3 mb-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">
                    {event.date}
                  </span>
                </div>
                <p className="font-semibold text-gray-900">{event.title}</p>
                {event.description && (
                  <p className="text-gray-600 mt-1 text-sm leading-relaxed">
                    {event.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
